/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {CategoriesLayout} from '../pages/categories/CategoriesLayout'
import {AddCategoryLayout} from '../pages/categories/AddCategoryLayout'
import {TagsLayout} from '../pages/tags/TagsLayout'
import {AddTagLayout} from '../pages/tags/AddTagLayout'
import {PlatformsLayout} from '../pages/platforms/PlatformsLayout'
import {AddPlatformLayout} from '../pages/platforms/AddPlatformLayout'
import {SubcategoriesLayout} from '../pages/subcategories/SubcategoriesLayout'
import {AddSubcategoryLayout} from '../pages/subcategories/AddSubcategoryLayout'
import DigitalAssetsPage from '../pages/digital-assets/DigitalAssetsPage'
import {UploadAssetLayout} from '../pages/digital-assets/UploadAssetLayout'
import {AssetDetailLayout} from '../pages/digital-assets/AssetDetailLayout'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser, auth} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {/* Landing page temporarily disabled */}
          {(currentUser || (auth && auth.access_token)) ? (
            <>
              <Route path='/' element={<Navigate to='dashboard' replace />} />
              <Route path='dashboard' element={<PrivateRoutes />} />
              <Route path='profile' element={<PrivateRoutes />} />
              {/* <Route path='settings/*' element={<PrivateRoutes />} /> */}
              <Route path='projects/*' element={<PrivateRoutes />} />
              <Route path='digital-assets/*' element={<PrivateRoutes />} />
              <Route path='categories' element={<CategoriesLayout />} />
              <Route path='categories/add' element={<AddCategoryLayout />} />
              <Route path='tags' element={<TagsLayout />} />
              <Route path='tags/add' element={<AddTagLayout />} />
              <Route path='platforms' element={<PlatformsLayout />} />
              <Route path='platforms/add' element={<AddPlatformLayout />} />
              <Route path='subcategories' element={<SubcategoriesLayout />} />
              <Route path='subcategories/add' element={<AddSubcategoryLayout />} />
              <Route path='digital-assets/upload' element={<UploadAssetLayout />} />
              <Route path='digital-assets/upload/:id' element={<UploadAssetLayout />} />
              <Route path='digital-assets/view/:id' element={<AssetDetailLayout />} />
              <Route path='roles/*' element={<PrivateRoutes />} />
              <Route path='users/*' element={<PrivateRoutes />} />
              <Route path='apps/*' element={<PrivateRoutes />} />
              <Route path='admin/*' element={<PrivateRoutes />} />
              <Route path='crafted/*' element={<PrivateRoutes />} />
              {/* Catch-all route for authenticated users - redirect to dashboard */}
              <Route path='*' element={<Navigate to='dashboard' replace />} />
            </>
          ) : (
            <>
              {/* Landing page disabled: redirect root to auth */}
              <Route path='/' element={<Navigate to='auth' replace />} />
              <Route path='auth/*' element={<AuthPage />} />
              {/* Catch-all route for non-authenticated users - redirect to auth */}
              <Route path='*' element={<Navigate to='auth' replace />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
